import type { LeadStatus } from '@/lib/api/types';

/**
 * Lead-type registry (plan §3.3 / spec §11). One engine serves all four lead types; they
 * differ by status set, columns, and which operations the backend exposes.
 */
export interface LeadTypeConfig {
  key: 'contact' | 'quotes' | 'applications' | 'newsletter';
  navKey: string;
  /**
   * English fallback label. Rendered copy is localized via `t(\`nav.${navKey}\`)` at the call
   * site (nav.contact/quotes/applications/newsletter already exist and match these strings).
   */
  label: string;
  /** Statuses offered in the pipeline selector (spec §11.1). */
  statuses: LeadStatus[];
  hasDetail: boolean;
  updatable: boolean; // PATCH status/assignee/notes
  hasExport: boolean;
  hasErasure: boolean; // PDPL DELETE (gap 15)
}

const CONTACT_STATUSES: LeadStatus[] = [
  'new',
  'in_progress',
  'reviewing',
  'responded',
  'closed',
  'spam',
  'archived',
];

/**
 * Y/DIRECTION (CMS plan D6) has one lead type: `contact` — the `/start`
 * discovery-call submission. Quotes / applications / newsletter are legacy-only and
 * dropped (no public form feeds them). `nav.ts` auto-derives, so the Leads group
 * shows just this entry. The whole spam / validation / export / PDPL-erasure
 * pipeline is reused unchanged.
 */
export const LEAD_TYPES: LeadTypeConfig[] = [
  {
    key: 'contact',
    navKey: 'contact',
    label: 'Discovery calls',
    statuses: CONTACT_STATUSES,
    hasDetail: true,
    updatable: true,
    hasExport: true,
    hasErasure: true,
  },
];

export function getLeadType(key: string): LeadTypeConfig | undefined {
  return LEAD_TYPES.find((t) => t.key === key);
}
